import axios from 'axios'
import { ENV } from '@/constants/env'
import { httpsStatus } from '@/constants/common'
import { useUserStore } from '@/store/useUserStore'
import { ACCESS_TOKEN_LOCAL_STORAGE } from '@/constants/localStorageKeys'
import { getDeviceId } from '../helpers/device'

let isRefreshing = false
let failedQueue: {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}[] = []

const processQueue = (error: null, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const instance = axios.create({
  baseURL: ENV.baseUrl,
  withCredentials: true,
})
const deviceId = getDeviceId()
instance.interceptors.request.use((config) => {
  if ((config.headers.v2 || config.url?.includes('v2')) && ENV.baseUrlV2) {
    config.baseURL = ENV.baseUrlV2
    config.url = config.url?.replace('v1', 'v2')
    delete config.headers.v2
  }
  const token = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE)
  config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Device-Id'] = deviceId
  return config
})

instance.interceptors.response.use(
  (config) => {
    return config
  },
  (error) => {
    const originalRequest = error.config
    if (error?.response?.status === 401) {
      localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE)
    }
    if (error?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject })
        })
          .then((accessToken) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + accessToken
            return axios(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      return new Promise(function (resolve, reject) {
        axios
          .post(
            `${ENV.baseUrl}/v2/public/auth/refresh-token`,
            {},
            { withCredentials: true },
          )
          .then(({ data }) => {
            localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE, data.accessToken)
            originalRequest.headers['Authorization'] =
              'Bearer ' + data.accessToken
            processQueue(null, data.accessToken)
            resolve(axios(originalRequest))
          })
          .catch((err) => {
            processQueue(err, null)
            if (error.response.status === httpsStatus.expired) {
              useUserStore.getState().setSessionTimeout(true)
            }
            reject(err)
          })
          .finally(() => {
            isRefreshing = false
          })
      })
    }

    return Promise.reject(error)
  },
)

export const api = instance
